/**
 * Settings → Accounting export: which software the accountant books in, and the numbers that
 * software needs - the cash account, a Sachkonto per card and bank account, the ranges for
 * Personenkonten and the tax codes. Every code shows the tool's standard as placeholder; only
 * what the Kanzlei set up differently is stored.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAccounts, useUpdateAccount } from "@/hooks/use-banking";
import { useUpdateWorkspace } from "@/hooks/use-workspaces";
import type { TaxTreatment } from "@/lib/at/vat";
import { formatRate } from "@/lib/utils/format";
import {
  BMD_TAX_CODES,
  DATEV_KEY_IDS,
  DATEV_KEYS,
  defaultRanges,
  type DatevKeyId,
  parseExportSettings,
  TAX_CODE_KEYS,
  type ExportSettings,
  type TaxCodeKey,
} from "@/lib/export/settings";
import { applyServerErrors } from "@/lib/utils/form-errors";
import { useWorkspace } from "@/lib/session/workspace-context";

/** Form field names cannot contain dots, so "in.standard" travels as "in_standard". */
const fieldOf = (key: TaxCodeKey) => key.replace(".", "_") as `${string}_${string}`;
const datevField = (id: DatevKeyId) => id.replace(/\./g, "_");

/** "in.standard.4.9" -> direction, treatment, rate (when the key depends on one). */
function datevParts(id: DatevKeyId) {
  const [direction, treatment, ...rate] = id.split(".");
  return {
    direction: direction as "in" | "out",
    treatment: treatment as TaxTreatment,
    rate: rate.length ? Number(rate.join(".")) : undefined,
  };
}

export function AccountingExportSettings() {
  const { t } = useTranslation();
  const { workspace, canManage } = useWorkspace();
  const { data: accounts } = useAccounts(workspace?.id);
  const updateWorkspace = useUpdateWorkspace();
  const updateAccount = useUpdateAccount();
  const current = parseExportSettings(workspace?.accountingExport);
  const bankAccounts = (accounts ?? []).filter((account) => !account.archived);

  const accountNumber = z
    .string()
    .trim()
    .regex(/^\d{0,20}$/, t("accountingExport.digitsOnly"));
  const schema = z.object({
    tool: z.enum(["none", "bmd", "rzl", "datev"]),
    cashAccount: accountNumber.min(1, t("accountingExport.digitsOnly")),
    supplierFrom: z.number().int().positive(),
    customerFrom: z.number().int().positive(),
    codes: z.record(
      z.string(),
      z
        .string()
        .trim()
        .regex(/^[0-9A-Za-z]{0,8}$/, t("accountingExport.codeFormat")),
    ),
    ledger: z.record(z.string(), accountNumber),
    datevKeys: z.record(
      z.string(),
      z
        .string()
        .trim()
        .regex(/^[0-9A-Za-z]{0,4}$/, t("accountingExport.codeFormat")),
    ),
    berater: z
      .string()
      .trim()
      .regex(/^(|\d{4,7})$/, t("accountingExport.digitsOnly")),
    mandant: z
      .string()
      .trim()
      .regex(/^\d{0,5}$/, t("accountingExport.digitsOnly")),
    accountLength: z.number().int().min(4).max(8),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    values: {
      tool: current.tool || "none",
      cashAccount: current.cashAccount,
      supplierFrom: current.supplierFrom,
      customerFrom: current.customerFrom,
      codes: Object.fromEntries(
        TAX_CODE_KEYS.map((key) => [fieldOf(key), current.taxCodes.bmd?.[key] ?? ""]),
      ),
      ledger: Object.fromEntries(
        bankAccounts.map((account) => [account.id, account.ledgerAccount ?? ""]),
      ),
      datevKeys: Object.fromEntries(
        DATEV_KEY_IDS.map((id) => [datevField(id), current.taxCodes.datev?.[id] ?? ""]),
      ),
      berater: current.datev.berater,
      mandant: current.datev.mandant,
      accountLength: current.datev.accountLength,
    },
  });

  const tool = useWatch({ control: form.control, name: "tool" });

  // Switching tools moves Personenkonto ranges that are still another tool's defaults: DATEV's
  // Kreditoren start at 70000, where BMD's 33000 would be a Debitor. Own ranges stay.
  function changeTool(next: Values["tool"]) {
    form.setValue("tool", next, { shouldDirty: true });
    const values = form.getValues();
    const isDefault = (["bmd", "datev"] as const).some((other) => {
      const ranges = defaultRanges(other);
      return (
        values.supplierFrom === ranges.supplierFrom && values.customerFrom === ranges.customerFrom
      );
    });
    if (!isDefault) return;
    const ranges = defaultRanges(next === "none" ? "" : next);
    form.setValue("supplierFrom", ranges.supplierFrom, { shouldDirty: true });
    form.setValue("customerFrom", ranges.customerFrom, { shouldDirty: true });
  }

  async function onSubmit(values: Values) {
    if (!workspace) return;
    const overrides: Partial<Record<TaxCodeKey, string>> = {};
    for (const key of TAX_CODE_KEYS) {
      const code = values.codes[fieldOf(key)]?.trim() ?? "";
      if (code && code !== BMD_TAX_CODES[key]) overrides[key] = code;
    }
    const datevOverrides: Partial<Record<DatevKeyId, string>> = {};
    for (const id of DATEV_KEY_IDS) {
      const key = values.datevKeys[datevField(id)]?.trim() ?? "";
      if (key && key !== DATEV_KEYS[id]) datevOverrides[id] = key;
    }
    const settings: ExportSettings = {
      tool: values.tool === "none" ? "" : values.tool,
      cashAccount: values.cashAccount,
      supplierFrom: values.supplierFrom,
      customerFrom: values.customerFrom,
      taxCodes: { bmd: overrides, datev: datevOverrides },
      datev: {
        berater: values.berater,
        mandant: values.mandant,
        accountLength: values.accountLength,
      },
    };
    try {
      await updateWorkspace.mutateAsync({
        id: workspace.id,
        input: { accountingExport: settings },
      });
      for (const account of bankAccounts) {
        const ledgerAccount = values.ledger[account.id] ?? "";
        if (ledgerAccount !== (account.ledgerAccount ?? "")) {
          await updateAccount.mutateAsync({ id: account.id, input: { ledgerAccount } });
        }
      }
      toast.success(t("settings.saved"));
    } catch (error) {
      const message = applyServerErrors(error, (name, err) =>
        form.setError(name as keyof Values, err),
      );
      if (message) toast.error(message);
    }
  }

  const numberField = (name: "supplierFrom" | "customerFrom", testId: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`accountingExport.${name}`)}</FormLabel>
          <FormControl>
            <Input
              type="number"
              inputMode="numeric"
              className="num"
              disabled={!canManage}
              data-testid={testId}
              {...field}
              onChange={(event) => field.onChange(event.target.valueAsNumber)}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="max-w-3xl space-y-8"
        data-testid="accounting-export-form"
      >
        <Alert>
          <AlertDescription>{t("accountingExport.beta")}</AlertDescription>
        </Alert>

        <section className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="tool"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("accountingExport.tool")}</FormLabel>
                <Select
                  onValueChange={(next) => changeTool(next as Values["tool"])}
                  value={field.value}
                  disabled={!canManage}
                >
                  <FormControl>
                    <SelectTrigger data-testid="export-tool">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">{t("accountingExport.tools.none")}</SelectItem>
                    <SelectItem value="bmd">{t("accountingExport.tools.bmd")}</SelectItem>
                    <SelectItem value="rzl">{t("accountingExport.tools.rzl")}</SelectItem>
                    <SelectItem value="datev">{t("accountingExport.tools.datev")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>{t("accountingExport.toolHint")}</FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cashAccount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("accountingExport.cashAccount")}</FormLabel>
                <FormControl>
                  <Input
                    className="num"
                    disabled={!canManage}
                    data-testid="cash-account"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {numberField("supplierFrom", "supplier-from")}
          {numberField("customerFrom", "customer-from")}
        </section>

        {bankAccounts.length > 0 && (
          <section className="space-y-3">
            <div>
              <h2 className="font-medium">{t("accountingExport.ledgerTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("accountingExport.ledgerHint")}</p>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableBody>
                  {bankAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">{account.label}</TableCell>
                      <TableCell className="w-40">
                        <FormField
                          control={form.control}
                          name={`ledger.${account.id}`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  className="num"
                                  aria-label={t("accountingExport.ledgerFor", {
                                    account: account.label,
                                  })}
                                  disabled={!canManage}
                                  data-testid="ledger-account"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {tool === "datev" && (
          <section className="space-y-4" data-testid="datev-settings">
            <h2 className="font-medium">{t("accountingExport.datevTitle")}</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {(["berater", "mandant"] as const).map((name) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t(`accountingExport.${name}`)}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          className="num"
                          disabled={!canManage}
                          data-testid={`datev-${name}`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <FormField
                control={form.control}
                name="accountLength"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("accountingExport.accountLength")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={4}
                        max={8}
                        className="num"
                        disabled={!canManage}
                        {...field}
                        onChange={(event) => field.onChange(event.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormDescription>{t("accountingExport.accountLengthHint")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <h3 className="text-sm font-medium">{t("accountingExport.datevKeysTitle")}</h3>
              <p className="text-sm text-muted-foreground">{t("accountingExport.datevKeysHint")}</p>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <Table data-testid="datev-keys">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("accountingExport.direction")}</TableHead>
                    <TableHead>{t("export.documents.taxTreatment")}</TableHead>
                    <TableHead className="w-32">{t("accountingExport.key")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {DATEV_KEY_IDS.map((id) => {
                    const { direction, treatment, rate } = datevParts(id);
                    const label = `${t(`taxTreatment.${treatment}`)}${rate !== undefined ? ` ${formatRate(rate)}` : ""}`;
                    return (
                      <TableRow key={id}>
                        <TableCell className="text-muted-foreground">
                          {t(`accountingExport.directions.${direction}`)}
                        </TableCell>
                        <TableCell>{label}</TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`datevKeys.${datevField(id)}`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    className="num"
                                    placeholder={DATEV_KEYS[id] || "—"}
                                    aria-label={`${t(`accountingExport.directions.${direction}`)} ${label}`}
                                    disabled={!canManage}
                                    data-testid={`datev-key-${id}`}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {tool === "rzl" && (
          <p className="text-sm text-muted-foreground" data-testid="rzl-codes">
            {t("accountingExport.rzlCodes")}
          </p>
        )}

        {tool === "bmd" && (
          <section className="space-y-3">
            <div>
              <h2 className="font-medium">{t("accountingExport.codesTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("accountingExport.codesHint")}</p>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <Table data-testid="tax-codes">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("accountingExport.direction")}</TableHead>
                    <TableHead>{t("export.documents.taxTreatment")}</TableHead>
                    <TableHead className="w-32">{t("accountingExport.code")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {TAX_CODE_KEYS.map((key) => {
                    const [direction, treatment] = key.split(".") as [
                      "in" | "out",
                      (typeof TAX_CODE_KEYS)[number] extends `${string}.${infer T}` ? T : never,
                    ];
                    return (
                      <TableRow key={key}>
                        <TableCell className="text-muted-foreground">
                          {t(`accountingExport.directions.${direction}`)}
                        </TableCell>
                        <TableCell>{t(`taxTreatment.${treatment}`)}</TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`codes.${fieldOf(key)}`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    className="num"
                                    placeholder={BMD_TAX_CODES[key] || "—"}
                                    aria-label={`${t(`accountingExport.directions.${direction}`)} ${t(`taxTreatment.${treatment}`)}`}
                                    disabled={!canManage}
                                    data-testid={`tax-code-${key}`}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {canManage && (
          <Button type="submit" disabled={form.formState.isSubmitting} data-testid="save-export">
            {t("common.save")}
          </Button>
        )}
      </form>
    </Form>
  );
}
